import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useDeferredValue, useMemo, useState } from 'react';
import { z } from 'zod';
import { useTodayView } from '~/api/derived';
import { useCan, useRoster } from '~/api/queries';
import { ChevronRight, PlusIcon } from '~/components/icons';
import { Avatar, AvatarDot, Chip, EmptyState, SearchField } from '~/components/ui';
import { matchesStudent } from '~/lib/search';
import { useSheet } from '~/lib/sheet';

export const Route = createFileRoute('/_app/_tabs/students')({
  validateSearch: z.object({ f: z.enum(['all', 'in', 'owes']).optional() }),
  component: Students,
});

const PAGE = 150;

function Students() {
  const navigate = useNavigate();
  const { f = 'all' } = Route.useSearch();
  const [query, setQuery] = useState('');
  const q = useDeferredValue(query.trim().toLowerCase());
  const [limit, setLimit] = useState(PAGE);
  const view = useTodayView();
  const roster = useRoster();
  const canRegister = useCan('register');
  const { open: openSheet } = useSheet();

  // Searching covers the whole institution (visitors from other centers); browsing shows this center.
  const base = q ? (roster.data ?? []) : view.centerRoster;
  const counts = useMemo(
    () => ({
      all: base.length,
      in: base.filter((s) => view.present.has(s.id)).length,
      owes: base.filter((s) => s.owes).length,
    }),
    [base, view.present],
  );
  const rows = useMemo(
    () =>
      base
        .filter((s) => matchesStudent(s, q))
        .filter((s) => f === 'all' || (f === 'in' && view.present.has(s.id)) || (f === 'owes' && s.owes))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [base, q, f, view.present],
  );

  return (
    <div className="flex min-h-0 flex-1 animate-fade-in-fast flex-col">
      <div className="shrink-0 px-5 pt-safe-6 pb-3">
        <div className="flex items-center justify-between">
          <div className="font-display text-[28px] font-bold tracking-[-0.02em]">Students</div>
          {canRegister && (
            <button
              type="button"
              aria-label="Register a student"
              onClick={() => openSheet('register')}
              className="flex h-[38px] w-[38px] cursor-pointer items-center justify-center rounded-xl border-none bg-brand-50 text-brand"
            >
              <PlusIcon />
            </button>
          )}
        </div>
        <SearchField value={query} onChange={setQuery} placeholder="Name, ref or school…" className="mt-3" />
        <div className="mt-3 flex gap-[7px] scroll-x">
          {(
            [
              ['all', 'All'],
              ['in', 'In today'],
              ['owes', 'Unpaid'],
            ] as const
          ).map(([id, label]) => (
            <Chip
              key={id}
              on={f === id}
              onClick={() =>
                void navigate({ to: '.', search: { f: id === 'all' ? undefined : id }, replace: true })
              }
            >
              {label} · {counts[id]}
            </Chip>
          ))}
        </div>
      </div>
      <div className="flex-1 px-5 pt-1 pb-nav scroll-y">
        {rows.slice(0, limit).map((s) => (
          <button
            type="button"
            key={s.id}
            onClick={() => void navigate({ to: '/student/$studentId', params: { studentId: s.id } })}
            className="flex w-full cursor-pointer items-center gap-3 border-0 border-b border-solid border-line-faint bg-transparent py-3 text-left"
          >
            <Avatar name={s.name} photoUrl={s.photoUrl} size={42} radius={13} fontSize={13.5}>
              <AvatarDot color={view.present.has(s.id) ? '#10b981' : '#cbd5e1'} />
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14.5px] font-semibold">{s.name}</div>
              <div className="mt-px truncate text-[12px] text-slate-400">
                {s.ref} · {s.school}
                {s.pending && <span className="font-semibold text-amber-600"> · waiting to sync</span>}
              </div>
            </div>
            {s.owes && (
              <span className="rounded-md bg-amber-100 px-[7px] py-[3px] text-[10.5px] font-bold text-amber-700">
                UNPAID
              </span>
            )}
            <ChevronRight />
          </button>
        ))}
        {rows.length > limit && (
          <button
            type="button"
            onClick={() => setLimit((l) => l + PAGE)}
            className="mt-3 w-full cursor-pointer rounded-xl border-none bg-fill py-3 text-[13px] font-semibold text-slate-600"
          >
            Show more · {rows.length - limit} left
          </button>
        )}
        {roster.data && rows.length === 0 && (
          <EmptyState>{q ? 'No student matches that search.' : 'No students here yet.'}</EmptyState>
        )}
      </div>
    </div>
  );
}
